import { AnimatePresence, MotionConfig, motion } from "motion/react";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { CheckIcon, LockIcon } from "./icons";

export type OtpStatus = "idle" | "verifying" | "success" | "error";

const LENGTH = 6;
/** One stable key per box: the boxes are fixed positions, never reordered. */
const SLOTS = ["d1", "d2", "d3", "d4", "d5", "d6"] as const;

/**
 * Six-digit code field for two-step verification. One real input covers six drawn boxes,
 * so paste, the authenticator's autofill and the phone's number pad all work as usual.
 * The sixth digit sends the code; a wrong one shakes red and clears for the next try.
 */
export function OtpInput({
  id,
  status,
  onComplete,
  onRetry,
}: {
  id: string;
  status: OtpStatus;
  onComplete: (code: string) => void;
  /** The person starts typing again after a wrong code: time to drop the error. */
  onRetry?: () => void;
}) {
  const [value, setValue] = useState("");
  const [focused, setFocused] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const locked = status === "verifying" || status === "success";
  // The error look lasts while the wrong digits are still there.
  const shown = status === "error" && value.length < LENGTH ? "idle" : status;

  useEffect(() => {
    if (status !== "error") return;
    const t = setTimeout(() => {
      setValue("");
      input.current?.focus();
    }, 700);
    return () => clearTimeout(t);
  }, [status]);

  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        className={`otp is-${shown}`}
        animate={shown === "error" ? { x: [0, -10, 10, -6, 6, -2, 0] } : { x: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <input
          ref={input}
          id={id}
          name="code"
          className="otp-input"
          value={value}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={LENGTH}
          required
          readOnly={locked}
          // biome-ignore lint/a11y/noAutofocus: the code is the only thing to do on this screen
          autoFocus
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "").slice(0, LENGTH);
            setValue(digits);
            if (status === "error") onRetry?.();
            if (digits.length === LENGTH) onComplete(digits);
          }}
        />
        {SLOTS.map((slot, i) => {
          const digit = value[i];
          const active = focused && !locked && i === Math.min(value.length, LENGTH - 1) && !digit;
          return (
            <motion.div
              key={slot}
              className={`otp-box${digit ? " filled" : ""}${active ? " active" : ""}`}
              style={{ "--i": i } as CSSProperties}
              animate={shown === "success" ? { y: [0, -8, 0] } : { y: 0 }}
              transition={{ duration: 0.4, delay: shown === "success" ? i * 0.06 : 0, ease: "easeOut" }}
              aria-hidden="true"
            >
              <AnimatePresence mode="popLayout" initial={false}>
                {digit ? (
                  <motion.span
                    key={`${slot}-${digit}`}
                    initial={{ y: 10, opacity: 0, scale: 0.6 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.6 }}
                    transition={{ type: "spring", stiffness: 600, damping: 26 }}
                  >
                    {digit}
                  </motion.span>
                ) : active ? (
                  <span key="caret" className="otp-caret" />
                ) : null}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </motion.div>
    </MotionConfig>
  );
}

/** Lock above the code: a ring spins while checking, it turns into a check when right. */
export function OtpBadge({ status }: { status: OtpStatus }) {
  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        className={`otp-badge is-${status}`}
        aria-hidden="true"
        animate={status === "error" ? { rotate: [0, -12, 12, -8, 8, 0] } : { rotate: 0 }}
        transition={{ duration: 0.45 }}
      >
        {status === "verifying" && (
          <motion.svg
            className="otp-ring"
            viewBox="0 0 76 76"
            animate={{ rotate: 360 }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 0.9, ease: "linear" }}
          >
            <circle
              cx="38"
              cy="38"
              r="36"
              fill="none"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="60 200"
            />
          </motion.svg>
        )}
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={status === "success" ? "check" : "lock"}
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.3, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 24 }}
          >
            {status === "success" ? <CheckIcon /> : <LockIcon />}
          </motion.span>
        </AnimatePresence>
      </motion.div>
    </MotionConfig>
  );
}
